import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from '../config/axiosClient';
import Config from '../config/Config.js';
import { RESOLVE_LOCAL_AUTH_STATE, RESOLVE_LOCAL_USER_STATE } from '../components/auth/AuthActions';
import SweetLoadingSpinner from '../components/eyeCandy/SweetLoadingSpinner.js';

const API_BASE = Config().url;
const REFRESH_INTERVAL = 30;

const MetricsPage = () => {
    const auth = RESOLVE_LOCAL_AUTH_STATE();
    const [data, setData] = useState(null);
    const [days, setDays] = useState(7);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [tab, setTab] = useState('health');
    const [newLogKeys, setNewLogKeys] = useState(new Set());
    const [countdown, setCountdown] = useState(REFRESH_INTERVAL);
    const intervalRef = useRef(null);
    const countdownRef = useRef(null);
    const prevLogKeysRef = useRef(new Set());

    const user = RESOLVE_LOCAL_USER_STATE();
    const isOwner = auth.is_authenticated === 'true' && user.pk === '1';

    const makeLogKey = (log) => `${log.timestamp}_${log.ip_address}_${log.path}_${log.method}`;

    const fetchData = useCallback(() => {
        return axios
            .get(`${API_BASE}api/v1/metrics/?days=${days}`, {
                headers: { Authorization: `Token ${auth.token}` },
            })
            .then((res) => {
                const newData = res.data;

                // Detect new log entries
                if (newData.recent_logs && prevLogKeysRef.current.size > 0) {
                    const fresh = new Set();
                    newData.recent_logs.forEach((log) => {
                        const key = makeLogKey(log);
                        if (!prevLogKeysRef.current.has(key)) {
                            fresh.add(key);
                        }
                    });
                    if (fresh.size > 0) {
                        setNewLogKeys(fresh);
                        setTimeout(() => setNewLogKeys(new Set()), 3000);
                    }
                }

                // Update prev keys
                if (newData.recent_logs) {
                    prevLogKeysRef.current = new Set(newData.recent_logs.map(makeLogKey));
                }

                setData(newData);
                setError(null);
                setLoading(false);
                setCountdown(REFRESH_INTERVAL);
            })
            .catch((err) => {
                setError(err.message || 'Failed to load metrics.');
                setLoading(false);
            });
    }, [days, auth.token]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (!isOwner) {
            setError('You don\u2019t have permission to view this page.');
            setLoading(false);
            return;
        }
        setLoading(true);
        setError(null);
        fetchData();
    }, [days, auth.token, isOwner, fetchData]);

    // Auto-refresh on Logs and Health tabs
    useEffect(() => {
        if ((tab === 'logs' || tab === 'health') && isOwner) {
            intervalRef.current = setInterval(() => { fetchData(); }, REFRESH_INTERVAL * 1000);
            countdownRef.current = setInterval(() => {
                setCountdown((c) => (c > 0 ? c - 1 : REFRESH_INTERVAL));
            }, 1000);
        }
        return () => {
            if (intervalRef.current) clearInterval(intervalRef.current);
            if (countdownRef.current) clearInterval(countdownRef.current);
        };
    }, [tab, days, isOwner, fetchData]);

    if (error) {
        return (
            <div className="container" style={{ paddingTop: 50 }}>
                <p style={{ color: '#991b1b' }}>{error}</p>
            </div>
        );
    }

    return (
        <div className="container" style={{ paddingTop: 50, paddingBottom: 60 }}>
            <h4 style={{ marginBottom: 20, color: 'var(--ink)' }}>Platform Metrics</h4>

            {/* Tab bar */}
            <div style={{ display: 'flex', gap: 0, marginBottom: 24, borderBottom: '2px solid #eee' }}>
                {['health', 'platform', 'traffic', 'logs'].map((t) => (
                    <button
                        key={t}
                        onClick={() => setTab(t)}
                        style={{
                            padding: '10px 24px',
                            border: 'none',
                            borderBottom: tab === t ? '2px solid var(--brand, #59c6c1)' : '2px solid transparent',
                            marginBottom: -2,
                            background: 'none',
                            cursor: 'pointer',
                            fontFamily: 'Courier',
                            fontSize: '0.9rem',
                            fontWeight: tab === t ? 700 : 400,
                            color: tab === t ? 'var(--brand, #59c6c1)' : 'var(--ink, #888)',
                        }}
                    >
                        {t.charAt(0).toUpperCase() + t.slice(1)}
                    </button>
                ))}
            </div>

            {loading && <SweetLoadingSpinner loadingState={true} />}

            {data && tab === 'health' && <HealthTab data={data} countdown={countdown} />}
            {data && tab === 'platform' && <PlatformTab data={data} />}
            {data && tab === 'traffic' && <TrafficTab data={data} days={days} setDays={setDays} />}
            {data && tab === 'logs' && <LogsTab data={data} days={days} setDays={setDays} newLogKeys={newLogKeys} countdown={countdown} makeLogKey={makeLogKey} />}
        </div>
    );
};

/* ===== HEALTH TAB ===== */

const STATUS_COLORS = {
    good: '#10b981',
    warn: '#f59e0b',
    bad: '#ef4444',
};

const getErrorRateStatus = (pct) => {
    if (pct < 1) return 'good';
    if (pct <= 5) return 'warn';
    return 'bad';
};

const getLatencyStatus = (ms) => {
    if (ms < 500) return 'good';
    if (ms <= 1000) return 'warn';
    return 'bad';
};

const HealthStatCard = ({ label, value, sub, status }) => {
    const color = STATUS_COLORS[status] || STATUS_COLORS.good;
    return (
        <div className="col-md-3 col-sm-6" style={{ marginBottom: 12 }}>
            <div
                style={{
                    background: 'var(--bg-soft, #f8f8f8)',
                    borderRadius: 8,
                    padding: '16px 20px',
                    textAlign: 'center',
                    borderLeft: `4px solid ${color}`,
                }}
            >
                <div style={{ fontSize: '1.6rem', fontWeight: 700, fontFamily: 'Courier', color: 'var(--ink, #222)' }}>{value}</div>
                <div style={{ fontSize: '0.8rem', color: '#888', marginTop: 4 }}>{label}</div>
                {sub && <div style={{ fontSize: '0.7rem', color: '#aaa', marginTop: 2 }}>{sub}</div>}
            </div>
        </div>
    );
};

const formatLastCheck = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    const now = new Date();
    const diffMs = now - d;
    const diffMin = Math.floor(diffMs / 60000);
    if (diffMin < 1) return 'just now';
    if (diffMin < 60) return `${diffMin}m ago`;
    const diffHr = Math.floor(diffMin / 60);
    if (diffHr < 24) return `${diffHr}h ago`;
    return `${Math.floor(diffHr / 24)}d ago`;
};

const HealthTab = ({ data, countdown }) => {
    const h = data.health;

    if (!h) {
        return <p style={{ color: '#888', fontFamily: 'Courier' }}>No health data available.</p>;
    }

    const errorRateStatus = getErrorRateStatus(h.error_rate_pct || 0);
    const latencyStatus = getLatencyStatus(h.latency_p95 || 0);
    const authTotal = (h.auth_failures_401 || 0) + (h.auth_failures_403 || 0);
    const authStatus = authTotal > 100 ? 'bad' : authTotal > 20 ? 'warn' : 'good';

    return (
        <>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', marginBottom: 16 }}>
                <div style={{ fontFamily: 'Courier', fontSize: '0.8rem', color: '#999' }}>
                    Refreshing in <span style={{ color: 'var(--brand, #59c6c1)', fontWeight: 700 }}>{countdown}s</span>
                </div>
            </div>

            {/* Status cards */}
            <div className="row" style={{ marginBottom: 30 }}>
                <HealthStatCard
                    label="Error Rate"
                    value={`${(h.error_rate_pct || 0).toFixed(1)}%`}
                    sub={`${h.errors_5xx || 0} server / ${h.errors_4xx || 0} client`}
                    status={errorRateStatus}
                />
                <HealthStatCard
                    label="Latency P95"
                    value={`${Math.round(h.latency_p95 || 0)}ms`}
                    sub={`P50: ${Math.round(h.latency_p50 || 0)}ms`}
                    status={latencyStatus}
                />
                <HealthStatCard
                    label="Auth Failures"
                    value={authTotal}
                    sub={`401: ${h.auth_failures_401} / 403: ${h.auth_failures_403}`}
                    status={authStatus}
                />
                <HealthStatCard
                    label="Uptime Checks"
                    value={h.healthchecks_ok}
                    sub={`Last: ${formatLastCheck(h.last_healthcheck)}`}
                    status="good"
                />
            </div>

            {/* Error trend */}
            {h.errors_per_day && h.errors_per_day.length > 0 && (
                <Section title="Error Trend (5xx per day)">
                    <SimpleBarChart
                        items={h.errors_per_day.map((d) => ({
                            label: d.date,
                            value: d.count_5xx,
                            sub: d.count_4xx > 0 ? `${d.count_4xx} 4xx` : '',
                        }))}
                        barColor="#ef4444"
                    />
                </Section>
            )}

            {/* Latency section */}
            <Section title="Latency">
                <div className="row">
                    <HealthStatCard
                        label="P50"
                        value={`${Math.round(h.latency_p50 || 0)}ms`}
                        status={getLatencyStatus(h.latency_p50 || 0)}
                    />
                    <HealthStatCard
                        label="P95"
                        value={`${Math.round(h.latency_p95 || 0)}ms`}
                        status={getLatencyStatus(h.latency_p95 || 0)}
                    />
                    <HealthStatCard
                        label="P99"
                        value={`${Math.round(h.latency_p99 || 0)}ms`}
                        status={getLatencyStatus(h.latency_p99 || 0)}
                    />
                </div>
            </Section>

            {/* Auth section */}
            <Section title="Auth">
                <div className="row" style={{ marginBottom: 20 }}>
                    <HealthStatCard
                        label="401 Unauthorized"
                        value={h.auth_failures_401}
                        status={h.auth_failures_401 > 50 ? 'bad' : h.auth_failures_401 > 10 ? 'warn' : 'good'}
                    />
                    <HealthStatCard
                        label="403 Forbidden"
                        value={h.auth_failures_403}
                        status={h.auth_failures_403 > 50 ? 'bad' : h.auth_failures_403 > 10 ? 'warn' : 'good'}
                    />
                </div>
                {h.errors_per_day && h.errors_per_day.length > 0 && (
                    <SimpleBarChart
                        items={h.errors_per_day.map((d) => ({
                            label: d.date,
                            value: d.count_auth,
                        }))}
                        barColor="#f59e0b"
                    />
                )}
            </Section>

            {/* Tokens section */}
            <Section title="Tokens">
                <div className="row">
                    <HealthStatCard
                        label="Total Tokens"
                        value={h.tokens_total}
                        status="good"
                    />
                    <HealthStatCard
                        label="Recently Created"
                        value={h.tokens_recent}
                        status="good"
                    />
                    <HealthStatCard
                        label="Expired"
                        value={h.tokens_expired}
                        status={h.tokens_expired > 200 ? 'warn' : 'good'}
                    />
                </div>
            </Section>
        </>
    );
};

/* ===== PLATFORM TAB ===== */

const PlatformTab = ({ data }) => (
    <>
        <div className="row" style={{ marginBottom: 30 }}>
            <StatCard label="Total Users" value={data.platform.total_users} />
            <StatCard label="Total Projects" value={data.platform.total_projects} />
            <StatCard label="Published" value={data.platform.published_projects} />
            <StatCard label="Unpublished" value={data.platform.unpublished_projects} />
        </div>

        <Section title="Projects By Owner (Top 5)">
            <SimpleBarChart
                items={data.platform.projects_by_owner.slice(0, 5).map((o) => ({
                    label: o.owner__username,
                    value: o.count,
                }))}
            />
        </Section>

        <Section title="Projects By Technology (Top 5)">
            <SimpleBarChart
                items={data.platform.projects_by_technology.slice(0, 5).map((t) => ({
                    label: t.technology,
                    value: t.count,
                }))}
            />
        </Section>

        <Section title="Projects By Skill (Top 5)">
            <SimpleBarChart
                items={data.platform.projects_by_skill.slice(0, 5).map((s) => ({
                    label: s.skill,
                    value: s.count,
                }))}
            />
        </Section>
    </>
);

/* ===== TRAFFIC TAB ===== */

const TrafficTab = ({ data, days, setDays }) => (
    <>
        <TimeSelector days={days} setDays={setDays} />

        <div className="row" style={{ marginBottom: 30 }}>
            <StatCard label={`Requests (${days}d)`} value={data.requests.total} />
            <StatCard label="Avg Response" value={data.requests.avg_response_ms !== null ? `${Math.round(data.requests.avg_response_ms)}ms` : 'N/A'} />
            <StatCard
                label="Auth / Anon"
                value={`${data.users.authenticated_vs_anonymous.authenticated} / ${data.users.authenticated_vs_anonymous.anonymous}`}
            />
            <StatCard label="Period" value={`${days} days`} />
        </div>

        <Section title="Requests Per Day">
            <SimpleBarChart
                items={data.requests.per_day.map((r) => ({
                    label: r.date,
                    value: r.count,
                    sub: r.avg_response_ms !== null ? `${Math.round(r.avg_response_ms)}ms avg` : '',
                }))}
            />
        </Section>

        <Section title="Top Endpoints (Top 5)">
            <table style={tableStyle}>
                <thead>
                    <tr>
                        <th style={thStyle}>Path</th>
                        <th style={{ ...thStyle, textAlign: 'right' }}>Hits</th>
                        <th style={{ ...thStyle, textAlign: 'right' }}>Avg ms</th>
                    </tr>
                </thead>
                <tbody>
                    {data.requests.top_endpoints.slice(0, 5).map((e, i) => (
                        <tr key={i}>
                            <td style={tdStyle}>{e.path}</td>
                            <td style={{ ...tdStyle, textAlign: 'right' }}>{e.count}</td>
                            <td style={{ ...tdStyle, textAlign: 'right' }}>{e.avg_response_ms !== null ? Math.round(e.avg_response_ms) : '-'}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </Section>

        <div className="row">
            <div className="col-md-6">
                <Section title="Status Codes">
                    <SimpleBarChart
                        items={data.requests.status_codes.map((s) => ({
                            label: String(s.status_code),
                            value: s.count,
                        }))}
                    />
                </Section>
            </div>
            <div className="col-md-6">
                <Section title="By Method">
                    <SimpleBarChart
                        items={data.requests.by_method.map((m) => ({
                            label: m.method,
                            value: m.count,
                        }))}
                    />
                </Section>
            </div>
        </div>

        <Section title="Top Active Users (Top 5)">
            <table style={tableStyle}>
                <thead>
                    <tr>
                        <th style={thStyle}>ID</th>
                        <th style={thStyle}>User</th>
                        <th style={thStyle}>Email</th>
                        <th style={{ ...thStyle, textAlign: 'right' }}>Requests</th>
                    </tr>
                </thead>
                <tbody>
                    {data.users.top_active.slice(0, 5).map((u, i) => (
                        <tr key={i}>
                            <td style={tdStyle}>{u.user__id}</td>
                            <td style={tdStyle}>{u.user__username}</td>
                            <td style={tdStyle}>{u.user__email}</td>
                            <td style={{ ...tdStyle, textAlign: 'right' }}>{u.count}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </Section>

        <Section title="Top IP Addresses (Top 5)">
            <table style={tableStyle}>
                <thead>
                    <tr>
                        <th style={thStyle}>IP Address</th>
                        <th style={thStyle}>User</th>
                        <th style={{ ...thStyle, textAlign: 'right' }}>Requests</th>
                    </tr>
                </thead>
                <tbody>
                    {data.top_ips.slice(0, 5).map((ip, i) => (
                        <tr key={i}>
                            <td style={tdStyle}>{ip.ip_address || 'unknown'}</td>
                            <td style={tdStyle}>{ip.user__username || 'anon'}</td>
                            <td style={{ ...tdStyle, textAlign: 'right' }}>{ip.count}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </Section>
    </>
);

/* ===== LOGS TAB ===== */

const LogsTab = ({ data, days, setDays, newLogKeys, countdown, makeLogKey }) => {
    const [copied, setCopied] = useState(false);

    const copyAsCsv = () => {
        if (!data.recent_logs || data.recent_logs.length === 0) return;
        const headers = ['timestamp', 'ip_address', 'user', 'method', 'path', 'status_code', 'response_time_ms'];
        const escape = (v) => {
            const s = String(v ?? '');
            return s.includes(',') || s.includes('"') || s.includes('\n') ? `"${s.replace(/"/g, '""')}"` : s;
        };
        const rows = data.recent_logs.map((log) => [
            log.timestamp,
            log.ip_address || '',
            log.user__username || 'anon',
            log.method,
            log.path,
            log.status_code,
            log.response_time_ms,
        ].map(escape).join(','));
        const csv = [headers.join(','), ...rows].join('\n');
        navigator.clipboard.writeText(csv).catch(() => {});
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    return (
    <>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
            <TimeSelector days={days} setDays={setDays} />
            <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                <button
                    onClick={copyAsCsv}
                    style={{
                        padding: '6px 14px',
                        border: '1px solid var(--border, #ddd)',
                        borderRadius: 4,
                        cursor: 'pointer',
                        fontFamily: 'Courier',
                        fontSize: '0.8rem',
                        backgroundColor: copied ? 'var(--brand, #59c6c1)' : 'transparent',
                        color: copied ? '#fff' : 'var(--ink, #333)',
                        transition: 'all 200ms ease',
                    }}
                >
                    {copied ? 'Copied!' : 'Copy as CSV'}
                </button>
                <div style={{ fontFamily: 'Courier', fontSize: '0.8rem', color: '#999' }}>
                    Refreshing in <span style={{ color: 'var(--brand, #59c6c1)', fontWeight: 700 }}>{countdown}s</span>
                </div>
            </div>
        </div>

        {data.recent_logs && (
            <table style={tableStyle}>
                <thead>
                    <tr>
                        <th style={thStyle}>Time</th>
                        <th style={thStyle}>IP</th>
                        <th style={thStyle}>User</th>
                        <th style={thStyle}>Method</th>
                        <th style={thStyle}>Path</th>
                        <th style={{ ...thStyle, textAlign: 'right' }}>Status</th>
                        <th style={{ ...thStyle, textAlign: 'right' }}>ms</th>
                    </tr>
                </thead>
                <tbody>
                    {data.recent_logs.map((log, i) => {
                        const isNew = newLogKeys.has(makeLogKey(log));
                        return (
                            <tr key={i} style={{
                                backgroundColor: isNew ? '#59c6c1' : 'transparent',
                                transition: 'background-color 1.5s ease-out',
                            }}>
                                <td style={{ ...tdStyle, whiteSpace: 'nowrap', fontSize: '0.8rem' }}>
                                    {new Date(log.timestamp).toLocaleString()}
                                </td>
                                <td style={tdStyle}>{log.ip_address || '-'}</td>
                                <td style={tdStyle}>{log.user__username || 'anon'}</td>
                                <td style={tdStyle}>{log.method}</td>
                                <td style={tdStyle}>{log.path}</td>
                                <td style={{ ...tdStyle, textAlign: 'right' }}>{log.status_code}</td>
                                <td style={{ ...tdStyle, textAlign: 'right' }}>{log.response_time_ms}</td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        )}
    </>
    );
};

/* ===== SHARED COMPONENTS ===== */

const TimeSelector = ({ days, setDays }) => (
    <div style={{ marginBottom: 20 }}>
        {[7, 14, 30, 90].map((d) => (
            <button
                key={d}
                onClick={() => setDays(d)}
                style={{
                    padding: '6px 14px',
                    marginRight: 6,
                    border: 'none',
                    borderRadius: 4,
                    cursor: 'pointer',
                    fontFamily: 'Courier',
                    fontSize: '0.85rem',
                    backgroundColor: days === d ? 'var(--brand, #59c6c1)' : 'var(--bg-soft, #f5f5f5)',
                    color: days === d ? '#fff' : 'var(--ink, #333)',
                }}
            >
                {d}d
            </button>
        ))}
    </div>
);

const StatCard = ({ label, value }) => (
    <div className="col-md-3 col-sm-6" style={{ marginBottom: 12 }}>
        <div
            style={{
                background: 'var(--bg-soft, #f8f8f8)',
                borderRadius: 8,
                padding: '16px 20px',
                textAlign: 'center',
            }}
        >
            <div style={{ fontSize: '1.6rem', fontWeight: 700, fontFamily: 'Courier', color: 'var(--ink, #222)' }}>{value}</div>
            <div style={{ fontSize: '0.8rem', color: '#888', marginTop: 4 }}>{label}</div>
        </div>
    </div>
);

const Section = ({ title, children }) => (
    <div style={{ marginBottom: 30 }}>
        <h6 style={{ color: 'var(--ink)', backgroundColor: 'var(--bg-soft)', padding: 5, display: 'inline-block' }}>{title}</h6>
        <hr style={{ marginTop: 4 }} />
        {children}
    </div>
);

const SimpleBarChart = ({ items, barColor }) => {
    if (!items || items.length === 0) return <p style={{ color: '#888' }}>No data</p>;
    const max = Math.max(...items.map((i) => i.value), 1);
    return (
        <div>
            {items.map((item, idx) => (
                <div key={idx} style={{ display: 'flex', alignItems: 'center', marginBottom: 6 }}>
                    <div style={{ width: 140, fontSize: '0.8rem', fontFamily: 'Courier', color: 'var(--ink, #333)', flexShrink: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {item.label}
                    </div>
                    <div style={{ flex: 1, marginLeft: 8, marginRight: 8 }}>
                        <div
                            style={{
                                height: 18,
                                width: `${(item.value / max) * 100}%`,
                                minWidth: 2,
                                backgroundColor: barColor || 'var(--brand, #59c6c1)',
                                borderRadius: 3,
                            }}
                        />
                    </div>
                    <div style={{ fontSize: '0.8rem', fontFamily: 'Courier', fontWeight: 600, minWidth: 40, textAlign: 'right' }}>
                        {item.value}
                    </div>
                    {item.sub && <div style={{ fontSize: '0.7rem', color: '#999', marginLeft: 6, minWidth: 60 }}>{item.sub}</div>}
                </div>
            ))}
        </div>
    );
};

const tableStyle = { width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem', fontFamily: 'Courier' };
const thStyle = { borderBottom: '2px solid #ddd', padding: '6px 10px', textAlign: 'left', color: '#888', fontWeight: 600 };
const tdStyle = { borderBottom: '1px solid #eee', padding: '6px 10px', color: 'var(--ink, #333)' };

export default MetricsPage;
